import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DotStepper } from './wizard/DotStepper';
import { WelcomeStep } from './wizard/WelcomeStep';
import { ThemeStep } from './wizard/ThemeStep';
import { OrganizationStep } from './wizard/OrganizationStep';
import { AgreementStep } from './wizard/AgreementStep';
import { CommunicationPreferencesStep } from './wizard/CommunicationPreferencesStep';
import { CompleteStep } from './wizard/CompleteStep';
import { useUpdateMe, getGetMeQueryKey } from '@/api/auth/self/self';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/components/theme-provider';
import { useOrgs } from '@/contexts/OrgsContext';
import { useAgreement } from '@/contexts/AgreementContext';
import { logger } from '@/services/logger';

type OnboardingStep = 'welcome' | 'theme' | 'organization' | 'agreement' | 'communicationPreferences' | 'complete';

interface UserOnboardingWizardProps {
  /** Called when onboarding is complete */
  onComplete: () => void;
  /** Whether the user has already completed onboarding (skips welcome, theme, complete steps) */
  isOnboarded?: boolean;
  /** Whether the user has active org memberships (skips org step if true) */
  hasActiveOrgs?: boolean;
}

/**
 * Multi-step wizard for user onboarding.
 * Guides new users through language selection, theme selection, and agreement acceptance.
 */
export function UserOnboardingWizard({
  onComplete,
  isOnboarded = false,
  hasActiveOrgs = false,
}: UserOnboardingWizardProps) {
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const { activeOrgs, pendingInvites, refetch: refetchOrgs } = useOrgs();
  const { pendingAgreements } = useAgreement();

  const [currentStep, setCurrentStep] = useState<OnboardingStep | null>(null);
  // Store step order once computed to prevent it from changing mid-wizard
  const [stepOrder, setStepOrder] = useState<OnboardingStep[] | null>(null);

  const updateMe = useUpdateMe();

  /**
   * Compute step order once on mount.
   * This is stored in state to prevent it from changing mid-wizard.
   */
  useEffect(() => {
    if (stepOrder === null) {
      const hasAgreements = pendingAgreements.length > 0;
      const userHasActiveOrgs = hasActiveOrgs || activeOrgs.length > 0;

      const steps: OnboardingStep[] = [];

      // Welcome, theme, and complete steps only for users who haven't onboarded
      if (!isOnboarded) {
        steps.push('welcome', 'theme');
      }

      // Organization step if user doesn't have active orgs (at wizard start)
      if (!isOnboarded || !userHasActiveOrgs) {
        steps.push('organization');
      }

      // Agreement step if there are pending agreements
      if (hasAgreements) {
        steps.push('agreement');
      }

      // Communication preferences step only for users who haven't onboarded
      if (!isOnboarded) {
        steps.push('communicationPreferences');
      }

      // Complete step only for users who haven't onboarded
      if (!isOnboarded) {
        steps.push('complete');
      }

      setStepOrder(steps);
      setCurrentStep(steps[0]);
    }
  }, []);

  const currentStepIndex = stepOrder && currentStep ? stepOrder.indexOf(currentStep) : -1;
  const isLastStep = stepOrder ? currentStepIndex === stepOrder.length - 1 : false;

  /**
   * Handle navigation to the next step
   */
  const handleNextStep = () => {
    if (!stepOrder) return;
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < stepOrder.length) {
      setCurrentStep(stepOrder[nextIndex]);
    }
  };

  /**
   * Handle navigation to the previous step
   */
  const handlePrevStep = () => {
    if (!stepOrder) return;
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(stepOrder[prevIndex]);
    }
  };

  /**
   * Check if back button should be shown for the current step
   */
  const canGoBack = currentStepIndex > 0;

  /**
   * Handle completion of the onboarding wizard.
   * Updates user preferences and marks onboarding as complete.
   */
  const handleComplete = async () => {
    try {
      logger.info('Starting onboarding completion', {
        context: 'UserOnboardingWizard.handleComplete',
        language,
        theme,
      });

      // Update user preferences and mark as onboarded
      await updateMe.mutateAsync({
        data: {
          preferredLanguage: language,
          theme: theme,
          onboarded: true,
        },
      });

      // Invalidate the /me query to refresh the user profile
      await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });

      logger.info('User onboarding completed', {
        context: 'UserOnboardingWizard.handleComplete',
        language,
        theme,
      });

      onComplete();
    } catch (error) {
      logger.error(error instanceof Error ? error : new Error('Failed to complete onboarding'), {
        context: 'UserOnboardingWizard.handleComplete',
      });
      toast.error(t.userOnboardingWizard.completeStep.error);
    }
  };

  /**
   * Handle agreement step completion (all agreements accepted)
   */
  const handleAgreementsAccepted = () => {
    if (isLastStep) {
      // No more steps - complete immediately without showing the "Get Started" screen
      onComplete();
    } else {
      handleNextStep();
    }
  };

  // Show loading state before steps are initialized
  if (stepOrder === null || currentStep === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-2xl text-center">
          <p className="text-muted-foreground">{t.userOnboardingWizard.agreementStep.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-2xl space-y-8">
        {/* Progress dots */}
        {stepOrder.length > 1 && <DotStepper totalSteps={stepOrder.length} currentStep={currentStepIndex} />}

        {/* Step content */}
        {currentStep === 'welcome' && <WelcomeStep onNext={handleNextStep} />}

        {currentStep === 'theme' && (
          <ThemeStep onNext={handleNextStep} onBack={handlePrevStep} />
        )}

        {currentStep === 'organization' && (
          <OrganizationStep
            activeOrgs={activeOrgs}
            pendingInvites={pendingInvites}
            onOrgsChanged={() => { void refetchOrgs(); }}
            onNext={isLastStep ? onComplete : handleNextStep}
            onBack={canGoBack ? handlePrevStep : undefined}
          />
        )}

        {currentStep === 'agreement' && (
          <AgreementStep
            pendingAgreements={pendingAgreements}
            onAllAccepted={handleAgreementsAccepted}
            onBack={canGoBack ? handlePrevStep : undefined}
          />
        )}

        {currentStep === 'communicationPreferences' && (
          <CommunicationPreferencesStep
            onNext={handleNextStep}
            onBack={canGoBack ? handlePrevStep : undefined}
          />
        )}

        {currentStep === 'complete' && (
          <CompleteStep
            isSubmitting={updateMe.isPending}
            onComplete={() => { void handleComplete(); }}
          />
        )}
      </div>
    </div>
  );
}
